import * as React from 'react'
import styled, { css } from 'styled-components'
import { INITIAL_UI_BACKGROUND, VIEW_MODE_LABELS, VIEW_MODES, ViewMode } from '../core/mdlConstants'
import { DatButton } from '../controls/DatButton'
import { DatColor } from '../controls/DatColor'
import { DatFile } from '../controls/DatFile'
import { DatFolder } from '../controls/DatFolder'
import { DatInput } from '../controls/DatInput'
import { DatItem } from '../controls/DatItem'
import { DatLabelText } from '../controls/DatLabelText'
import { DatRange } from '../controls/DatRange'
import { DatSelect } from '../controls/DatSelect'
import { DatWrapper } from '../controls/DatWrapper'
import { ModelController, ModelState } from '../core/modelController'
import { ModelData } from '../core/modelDataParser'
import { ControllerContainer } from './ControllerContainer'
import { createEmbedUrl, createShareableUrl, type CameraUrlPreset, type PlaybackUrlPreset } from './urlConfig'
import type { ViewerPose } from './Renderer'

const DRAG_BOUNDARY_GAP = 10

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

type DragSession = {
  handle: HTMLElement
  pointerId: number
  startX: number
  startY: number
  originX: number
  originY: number
  panelLeft: number
  panelTop: number
  panelWidth: number
  panelHeight: number
}

const useDraggablePanel = () => {
  const panelRef = React.useRef<HTMLDivElement>(null)
  const dragSessionRef = React.useRef<DragSession | null>(null)
  const offsetRef = React.useRef({ x: 0, y: 0 })
  const pendingOffsetRef = React.useRef<{ x: number; y: number } | null>(null)
  const frameRef = React.useRef(0)
  const [isDragging, setIsDragging] = React.useState(false)

  const applyOffset = React.useCallback((x: number, y: number) => {
    const panel = panelRef.current

    if (!panel) {
      return
    }

    offsetRef.current = { x, y }
    panel.style.setProperty('--panel-drag-x', `${x}px`)
    panel.style.setProperty('--panel-drag-y', `${y}px`)
  }, [])

  const scheduleOffset = React.useCallback(
    (x: number, y: number) => {
      pendingOffsetRef.current = { x, y }

      if (frameRef.current !== 0) {
        return
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0

        const nextOffset = pendingOffsetRef.current
        pendingOffsetRef.current = null

        if (nextOffset) {
          applyOffset(nextOffset.x, nextOffset.y)
        }
      })
    },
    [applyOffset]
  )

  const clampOffsetToViewport = React.useCallback(
    (x: number, y: number, session: DragSession) => {
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const maxLeft = Math.max(DRAG_BOUNDARY_GAP, viewportWidth - session.panelWidth - DRAG_BOUNDARY_GAP)
      const maxTop = Math.max(DRAG_BOUNDARY_GAP, viewportHeight - session.panelHeight - DRAG_BOUNDARY_GAP)
      const nextLeft = session.panelLeft + x - session.originX
      const nextTop = session.panelTop + y - session.originY
      const clampedLeft = clamp(nextLeft, DRAG_BOUNDARY_GAP, maxLeft)
      const clampedTop = clamp(nextTop, DRAG_BOUNDARY_GAP, maxTop)

      return {
        x: session.originX + clampedLeft - session.panelLeft,
        y: session.originY + clampedTop - session.panelTop
      }
    },
    []
  )

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!event.isPrimary || event.button !== 0 || !panelRef.current) {
      return
    }

    event.preventDefault()

    const panelRect = panelRef.current.getBoundingClientRect()
    const offset = offsetRef.current

    dragSessionRef.current = {
      handle: event.currentTarget,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
      panelLeft: panelRect.left,
      panelTop: panelRect.top,
      panelWidth: panelRect.width,
      panelHeight: panelRect.height
    }

    event.currentTarget.setPointerCapture(event.pointerId)
    setIsDragging(true)
  }, [])

  const finishDrag = React.useCallback((pointerId: number) => {
    const session = dragSessionRef.current

    if (!session || session.pointerId !== pointerId) {
      return
    }

    if (session.handle.hasPointerCapture(pointerId)) {
      session.handle.releasePointerCapture(pointerId)
    }

    dragSessionRef.current = null
    setIsDragging(false)
  }, [])

  const handleLostPointerCapture = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    const session = dragSessionRef.current

    if (!session || session.pointerId !== event.pointerId) {
      return
    }

    dragSessionRef.current = null
    setIsDragging(false)
  }, [])

  React.useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      const session = dragSessionRef.current

      if (!session || session.pointerId !== event.pointerId) {
        return
      }

      event.preventDefault()

      const nextOffset = clampOffsetToViewport(
        session.originX + event.clientX - session.startX,
        session.originY + event.clientY - session.startY,
        session
      )

      scheduleOffset(nextOffset.x, nextOffset.y)
    }

    const handleWindowPointerEnd = (event: PointerEvent) => {
      finishDrag(event.pointerId)
    }

    const keepPanelInViewport = () => {
      const panel = panelRef.current

      if (!panel) {
        return
      }

      const rect = panel.getBoundingClientRect()
      const offset = offsetRef.current
      const maxLeft = Math.max(DRAG_BOUNDARY_GAP, window.innerWidth - rect.width - DRAG_BOUNDARY_GAP)
      const maxTop = Math.max(DRAG_BOUNDARY_GAP, window.innerHeight - rect.height - DRAG_BOUNDARY_GAP)
      const clampedLeft = clamp(rect.left, DRAG_BOUNDARY_GAP, maxLeft)
      const clampedTop = clamp(rect.top, DRAG_BOUNDARY_GAP, maxTop)

      applyOffset(offset.x + clampedLeft - rect.left, offset.y + clampedTop - rect.top)
    }

    window.addEventListener('pointermove', handleWindowPointerMove, { passive: false })
    window.addEventListener('pointerup', handleWindowPointerEnd)
    window.addEventListener('pointercancel', handleWindowPointerEnd)
    window.addEventListener('resize', keepPanelInViewport)

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove)
      window.removeEventListener('pointerup', handleWindowPointerEnd)
      window.removeEventListener('pointercancel', handleWindowPointerEnd)
      window.removeEventListener('resize', keepPanelInViewport)

      if (frameRef.current !== 0) {
        window.cancelAnimationFrame(frameRef.current)
      }
    }
  }, [applyOffset, clampOffsetToViewport, finishDrag, scheduleOffset])

  const resetOffset = React.useCallback(() => {
    pendingOffsetRef.current = null

    if (frameRef.current !== 0) {
      window.cancelAnimationFrame(frameRef.current)
      frameRef.current = 0
    }

    applyOffset(0, 0)
  }, [applyOffset])

  return {
    handlePointerDown,
    handleLostPointerCapture,
    isDragging,
    panelRef,
    resetOffset
  }
}

const StyledDatGui = styled(DatWrapper)<{ $isCompact: boolean; $isDragging: boolean }>`
  --panel-drag-x: 0px;
  --panel-drag-y: 0px;

  position: fixed;
  top: ${props => (props.$isCompact ? '58px' : '62px')};
  right: ${props => (props.$isCompact ? '10px' : '14px')};
  z-index: 4;
  width: ${props => (props.$isCompact ? 'min(264px, calc(100vw - 20px))' : 'min(340px, calc(100vw - 28px))')};
  max-width: calc(100vw - 20px);
  max-height: ${props => (props.$isCompact ? 'calc(100dvh - 20px)' : 'calc(100dvh - 28px)')};
  gap: ${props => (props.$isCompact ? '6px' : '12px')};
  padding: ${props => (props.$isCompact ? '8px' : '12px')};
  border: 1px solid rgba(148, 163, 184, ${props => (props.$isCompact ? '0.1' : '0.14')});
  border-radius: ${props => (props.$isCompact ? '12px' : '22px')};
  background: rgba(15, 23, 42, ${props => (props.$isCompact ? '0.72' : '0.82')});
  box-shadow: ${props => (props.$isCompact ? '0 10px 26px rgba(2, 6, 23, 0.24)' : '0 18px 50px rgba(2, 6, 23, 0.34)')};
  backdrop-filter: blur(${props => (props.$isCompact ? '12px' : '18px')});
  overflow-y: auto;
  overscroll-behavior: contain;
  transform: translate3d(var(--panel-drag-x), var(--panel-drag-y), 0);
  will-change: transform;
  contain: layout paint;
  user-select: ${props => (props.$isDragging ? 'none' : 'auto')};

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.18);
  }

  @media (max-width: 900px) {
    top: auto;
    right: ${props => (props.$isCompact ? '10px' : '14px')};
    bottom: ${props => (props.$isCompact ? '10px' : '14px')};
    max-height: ${props => (props.$isCompact ? 'min(50dvh, 420px)' : 'min(60dvh, 520px)')};
  }

  @media (max-width: 520px) {
    top: auto;
    right: 8px;
    bottom: calc(8px + env(safe-area-inset-bottom));
    left: 8px;
    width: auto;
    max-width: none;
    max-height: min(64dvh, calc(100dvh - 16px - env(safe-area-inset-bottom)));
    padding: 8px;
    border-radius: 10px;
  }

  @media (max-height: 520px) and (min-width: 521px) {
    top: 8px;
    right: 8px;
    bottom: auto;
    max-height: calc(100dvh - 16px);
  }

  @media (max-height: 380px) {
    max-height: calc(100dvh - 16px);
  }

  ${props =>
    props.$isCompact &&
    css`
      section {
        gap: 5px;
      }

      label,
      button {
        min-height: 30px;
        gap: 7px;
        padding: 4px 7px 4px 9px;
        border-color: rgba(148, 163, 184, 0.09);
        border-radius: 9px;
        background: rgba(15, 23, 42, 0.42);
        box-shadow: none;
      }

      label::before,
      button::before {
        display: none;
      }

      label:hover,
      button:hover {
        border-color: rgba(148, 163, 184, 0.16);
        background: rgba(15, 23, 42, 0.56);
      }

      label > span:first-child {
        flex: 1 1 34%;
        font-size: 0.68rem;
        font-weight: 560;
        letter-spacing: 0;
        color: rgba(226, 232, 240, 0.72);
      }

      input:not([type='file']):not([type='range']),
      select {
        min-height: 26px;
        padding: 3px 8px;
        border-radius: 7px;
        font-size: 0.72rem;
        background: rgba(2, 6, 23, 0.34);
      }

      select {
        padding-right: 26px;
      }

      input[type='color'] {
        width: 26px;
        height: 26px;
        padding: 2px;
      }

      input[type='range'] {
        height: 14px;
      }

      label > div {
        gap: 7px;
      }

      label > div::after {
        right: 9px;
        width: 6px;
        height: 6px;
      }

      button {
        font-size: 0.74rem;
        font-weight: 600;
      }

      @media (max-width: 520px) {
        label > span:first-child {
          flex: 1 0 100%;
        }

        label > div {
          flex-basis: 100%;
        }
      }
    `}
`

const Header = styled.header<{ $isCompact: boolean; $isDragging: boolean }>`
  display: grid;
  gap: ${props => (props.$isCompact ? '0' : '4px')};
  padding: ${props => (props.$isCompact ? '0 2px 2px' : '2px 2px 8px')};
  cursor: ${props => (props.$isDragging ? 'grabbing' : 'grab')};
  touch-action: none;
  user-select: none;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`

const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
`

const Title = styled.h2`
  min-width: 0;
  font-size: 0.88rem;
  font-weight: 650;
  letter-spacing: 0;
  color: #f8fafc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Status = styled.span<{ $isCompact: boolean }>`
  flex: 0 0 auto;
  font-size: ${props => (props.$isCompact ? '0' : '0.72rem')};
  width: ${props => (props.$isCompact ? '7px' : 'auto')};
  height: ${props => (props.$isCompact ? '7px' : 'auto')};
  border-radius: 999px;
  background: ${props => (props.$isCompact ? 'rgba(125, 211, 252, 0.78)' : 'transparent')};
  color: rgba(148, 163, 184, 0.74);
`

const Meta = styled.p`
  font-size: 0.78rem;
  line-height: 1.5;
  color: rgba(148, 163, 184, 0.78);
`

const ArrowButton = styled.button`
  && {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    min-height: 30px;
    padding: 0;
    border: 1px solid rgba(148, 163, 184, 0.12);
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.5);
    color: rgba(248, 250, 252, 0.92);
    box-shadow: none;
    cursor: pointer;
  }

  &&:hover {
    border-color: rgba(148, 163, 184, 0.22);
    background: rgba(30, 41, 59, 0.82);
  }

  &&:focus-visible {
    outline: none;
    border-color: rgba(147, 197, 253, 0.36);
    box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.12);
  }
`

const RestoreMenuButton = styled(ArrowButton)`
  && {
    position: fixed;
    top: 62px;
    right: 14px;
    z-index: 5;
    width: 42px;
    height: 42px;
    min-height: 42px;
    border-radius: 14px;
    background: rgba(15, 23, 42, 0.78);
    box-shadow: 0 14px 34px rgba(2, 6, 23, 0.28);
    backdrop-filter: blur(14px);
  }

  @media (max-width: 900px) {
    && {
      top: auto;
      right: 14px;
      bottom: 14px;
    }
  }

  @media (max-width: 520px) {
    && {
      right: 8px;
      bottom: calc(8px + env(safe-area-inset-bottom));
      width: 40px;
      height: 40px;
      min-height: 40px;
      border-radius: 10px;
    }
  }

  @media (max-height: 520px) and (min-width: 521px) {
    && {
      top: 8px;
      right: 8px;
      bottom: auto;
    }
  }
`

const ArrowIcon = styled.span<{ $direction: 'left' | 'right' }>`
  width: 8px;
  height: 8px;
  border-top: 2px solid currentColor;
  border-right: 2px solid currentColor;
  transform: rotate(${props => (props.$direction === 'right' ? '45deg' : '-135deg')});
`

const Callout = styled.div`
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.44);
  font-size: 0.78rem;
  line-height: 1.55;
  color: rgba(148, 163, 184, 0.76);
`

const SequenceStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
  padding: 6px 7px;
  border: 1px solid rgba(148, 163, 184, 0.09);
  border-radius: 9px;
  background: rgba(15, 23, 42, 0.36);

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const SequenceStat = styled.span`
  display: grid;
  gap: 1px;
  min-width: 0;
  font-size: 0.62rem;
  line-height: 1.2;
  color: rgba(148, 163, 184, 0.72);
`

const SequenceStatValue = styled.strong`
  min-width: 0;
  overflow: hidden;
  color: rgba(226, 232, 240, 0.9);
  font-family: 'IBM Plex Mono', 'Consolas', 'SFMono-Regular', monospace;
  font-size: 0.72rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PoseItem = styled(DatItem)`
  --dat-accent: rgba(125, 211, 252, 0.36);
`

const PoseInput = styled(DatInput)`
  flex: 1 1 62%;
  font-family: 'IBM Plex Mono', 'Consolas', 'SFMono-Regular', monospace;
  font-size: 0.74rem;
  text-overflow: ellipsis;

  @media (max-width: 520px) {
    flex-basis: 100%;
  }
`

const EmbedCodeBlock = styled.pre`
  margin: 0;
  max-height: 170px;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.48);
  color: rgba(226, 232, 240, 0.9);
  font-family: 'IBM Plex Mono', 'Consolas', 'SFMono-Regular', monospace;
  font-size: 0.68rem;
  line-height: 1.5;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`

const formatPoseValue = (value: number) => {
  const rounded = Number(value.toFixed(3))
  const normalized = Object.is(rounded, -0) ? 0 : rounded

  return Number.isInteger(normalized)
    ? String(normalized)
    : normalized.toFixed(3).replace(/\.?0+$/, '')
}

const formatPoseVector = (vector: [number, number, number]) => vector.map(formatPoseValue).join(',')

const writeClipboardText = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // Fall back to a temporary selection below when Clipboard API is blocked.
    }
  }

  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.setAttribute('readonly', 'true')
  textArea.style.position = 'fixed'
  textArea.style.left = '-9999px'
  document.body.append(textArea)
  textArea.select()
  document.execCommand('copy')
  textArea.remove()
}

const createPoseQuery = (viewerPose: ViewerPose, viewMode: ViewMode) => {
  const params = [
    ['viewtype', viewMode === 'weapon-origin' ? 'weapon' : 'free'],
    ['position', formatPoseVector(viewerPose.origin)],
    ['angles', formatPoseVector(viewerPose.angles)],
    ['target', formatPoseVector(viewerPose.target)]
  ]

  return params
    .map(([key, value]) => `${key}=${encodeURIComponent(value).replace(/%2C/g, ',')}`)
    .join('&')
}

const escapeHtmlAttribute = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

const createIframeCode = (url: string) =>
  `<iframe src="${escapeHtmlAttribute(url)}" width="100%" height="480" style="border:0;" loading="lazy" allowfullscreen></iframe>`

const PoseValue = (props: { label: string; value: string }) => (
  <PoseItem>
    <DatLabelText>{props.label}</DatLabelText>
    <PoseInput value={props.value} readOnly onFocus={event => event.currentTarget.select()} />
  </PoseItem>
)

const countMeshesPolygons = (meshes: ModelData['meshes'][number][number]) =>
  meshes.reduce((count, mesh) => count + mesh.numTris, 0)

const countVisiblePolygons = (modelData: ModelData, visibleSubModelIndices: number[]) =>
  modelData.meshes.reduce((count, bodyPartMeshes, bodyPartIndex) => {
    const visibleSubModelMeshes = bodyPartMeshes[visibleSubModelIndices[bodyPartIndex] ?? 0] ?? bodyPartMeshes[0]

    return count + (visibleSubModelMeshes ? countMeshesPolygons(visibleSubModelMeshes) : 0)
  }, 0)

const formatCount = (value: number) => value.toLocaleString('en-US')

const createPlaybackPresetFromModelState = (modelState: ModelState): PlaybackUrlPreset => ({
  sequenceIndex:          modelState.activeAnimationIndex,
  frame:                  modelState.frame,
  isPaused:               modelState.isPaused,
  playbackRate:           modelState.playbackRate,
  visibleSubModelIndices: [...modelState.visibleSubModelIndices]
})

const createCameraPresetFromViewerPose = (viewerPose?: ViewerPose): CameraUrlPreset | undefined =>
  viewerPose
    ? {
      position: viewerPose.origin,
      angles:   viewerPose.angles,
      target:   viewerPose.target
    }
    : undefined

type Props = {
  backgroundColor: string
  embedModelUrl?: string
  modelController?: ModelController
  modelData?: ModelData
  shareableModelUrl?: string
  viewerPose?: ViewerPose
  viewMode: ViewMode
  isLoading: boolean
  onBackgroundColorUpdate: (color: string) => void
  onModelLoad: (file: File) => void
  onViewModeChange: (viewMode: ViewMode) => void
}

export const Controller = (props: Props) => {
  const { backgroundColor, modelController, modelData, isLoading } = props
  const draggablePanel = useDraggablePanel()
  const [isMenuHidden, setIsMenuHidden] = React.useState(false)
  const hasModel = Boolean(modelController && modelData)
  const isShareable = Boolean(props.shareableModelUrl && hasModel)
  const viewerPose = props.viewerPose
  const title = hasModel && modelData ? modelData.header.name : 'Model'
  const meta = hasModel && modelData
    ? `${modelData.sequences.length} sequences | ${modelData.bodyParts.length} body parts | ${modelData.textures.length} textures`
    : 'Upload a `.mdl` to open the playback controls.'
  const originValue = viewerPose ? formatPoseVector(viewerPose.origin) : ''
  const anglesValue = viewerPose ? formatPoseVector(viewerPose.angles) : ''
  const targetValue = viewerPose ? formatPoseVector(viewerPose.target) : ''
  const [sharePlaybackPreset, setSharePlaybackPreset] = React.useState<PlaybackUrlPreset>()
  const [hasCopiedEmbedCode, setHasCopiedEmbedCode] = React.useState(false)
  const [hasCopiedShareUrl, setHasCopiedShareUrl] = React.useState(false)
  const embedCopyResetTimeoutRef = React.useRef(0)
  const copyResetTimeoutRef = React.useRef(0)
  const shareableUrl = React.useMemo(() => {
    if (!isShareable || !props.shareableModelUrl) {
      return undefined
    }

    return createShareableUrl({
      modelUrl:        props.shareableModelUrl,
      viewMode:        props.viewMode,
      backgroundColor,
      playbackPreset:  sharePlaybackPreset,
      cameraPreset:    createCameraPresetFromViewerPose(viewerPose)
    })
  }, [backgroundColor, isShareable, props.shareableModelUrl, props.viewMode, sharePlaybackPreset, viewerPose])
  const embedShareableUrl = React.useMemo(() => {
    if (!isShareable || !props.embedModelUrl) {
      return undefined
    }

    return createShareableUrl({
      modelUrl:        props.embedModelUrl,
      viewMode:        props.viewMode,
      backgroundColor,
      playbackPreset:  sharePlaybackPreset,
      cameraPreset:    createCameraPresetFromViewerPose(viewerPose)
    })
  }, [backgroundColor, isShareable, props.embedModelUrl, props.viewMode, sharePlaybackPreset, viewerPose])
  const embedUrl = React.useMemo(() => (
    embedShareableUrl ? createEmbedUrl(embedShareableUrl) : undefined
  ), [embedShareableUrl])
  const embedIframeCode = React.useMemo(() => (
    embedUrl ? createIframeCode(embedUrl) : undefined
  ), [embedUrl])

  React.useEffect(() => {
    if (!isShareable || !modelController) {
      setSharePlaybackPreset(undefined)
      return
    }

    setSharePlaybackPreset(createPlaybackPresetFromModelState(modelController.getCurrentState()))
  }, [isShareable, modelController])

  React.useEffect(() => {
    if (!shareableUrl) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      if (window.location.href !== shareableUrl) {
        window.history.replaceState(window.history.state, '', shareableUrl)
      }
    }, 120)

    return () => window.clearTimeout(timeoutId)
  }, [shareableUrl])

  React.useEffect(() => {
    setHasCopiedShareUrl(false)
  }, [shareableUrl])

  React.useEffect(() => {
    setHasCopiedEmbedCode(false)
  }, [embedIframeCode])

  React.useEffect(() => () => {
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current)
    }

    if (embedCopyResetTimeoutRef.current) {
      window.clearTimeout(embedCopyResetTimeoutRef.current)
    }
  }, [])

  const handleModelStateEdit = React.useCallback((modelState: ModelState) => {
    setSharePlaybackPreset(createPlaybackPresetFromModelState(modelState))
  }, [])

  const handleCopyShareUrl = React.useCallback(() => {
    if (!shareableUrl) {
      return
    }

    void writeClipboardText(shareableUrl).then(() => {
      setHasCopiedShareUrl(true)

      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current)
      }

      copyResetTimeoutRef.current = window.setTimeout(() => {
        setHasCopiedShareUrl(false)
      }, 1600)
    })
  }, [shareableUrl])

  const handleCopyEmbedCode = React.useCallback(() => {
    if (!embedIframeCode) {
      return
    }

    void writeClipboardText(embedIframeCode).then(() => {
      setHasCopiedEmbedCode(true)

      if (embedCopyResetTimeoutRef.current) {
        window.clearTimeout(embedCopyResetTimeoutRef.current)
      }

      embedCopyResetTimeoutRef.current = window.setTimeout(() => {
        setHasCopiedEmbedCode(false)
      }, 1600)
    })
  }, [embedIframeCode])

  if (isMenuHidden) {
    return (
      <RestoreMenuButton
        type="button"
        title="Mostrar menu"
        aria-label="Mostrar menu"
        onClick={() => setIsMenuHidden(false)}
      >
        <ArrowIcon $direction="left" aria-hidden="true" />
      </RestoreMenuButton>
    )
  }

  return (
    <StyledDatGui
      ref={draggablePanel.panelRef}
      $isCompact={hasModel}
      $isDragging={draggablePanel.isDragging}
      data-testid="options-menu"
    >
      <Header
        $isCompact={hasModel}
        $isDragging={draggablePanel.isDragging}
        title="Arrastar menu"
        onPointerDown={draggablePanel.handlePointerDown}
        onLostPointerCapture={draggablePanel.handleLostPointerCapture}
      >
        <TitleRow>
          <Title>{title}</Title>
          <HeaderActions>
            <Status $isCompact={hasModel} title={hasModel ? 'ready' : 'idle'}>
              {hasModel ? 'ready' : 'idle'}
            </Status>
            <ArrowButton
              type="button"
              title="Esconder menu"
              aria-label="Esconder menu"
              onPointerDown={event => event.stopPropagation()}
              onClick={() => {
                draggablePanel.resetOffset()
                setIsMenuHidden(true)
              }}
            >
              <ArrowIcon $direction="right" aria-hidden="true" />
            </ArrowButton>
          </HeaderActions>
        </TitleRow>
        {!hasModel ? <Meta>{meta}</Meta> : null}
      </Header>

      <DatFile label={hasModel ? 'Model' : 'File'} onChange={file => props.onModelLoad(file)} />

      <DatColor label="Background" value={backgroundColor} onChange={color => props.onBackgroundColorUpdate(color)} />

      <DatSelect
        label="View"
        activeItemIndex={VIEW_MODES.indexOf(props.viewMode)}
        items={VIEW_MODES.map(mode => VIEW_MODE_LABELS[mode])}
        onChange={selectedIndex => props.onViewModeChange(VIEW_MODES[selectedIndex] ?? VIEW_MODES[0])}
      />

      {shareableUrl ? (
        <DatButton onClick={handleCopyShareUrl}>{hasCopiedShareUrl ? 'Copied URL' : 'Copy share URL'}</DatButton>
      ) : null}

      {embedIframeCode ? (
        <DatFolder title="Embed" defaultHidden>
          <EmbedCodeBlock>
            <code>{embedIframeCode}</code>
          </EmbedCodeBlock>
          <DatButton onClick={handleCopyEmbedCode}>
            {hasCopiedEmbedCode ? 'Copied iframe code' : 'Copy iframe code'}
          </DatButton>
        </DatFolder>
      ) : null}

      {backgroundColor !== INITIAL_UI_BACKGROUND && (
        <DatButton onClick={() => props.onBackgroundColorUpdate(INITIAL_UI_BACKGROUND)}>Reset background</DatButton>
      )}

      {!hasModel && isLoading ? <Callout>Preparing geometry and textures.</Callout> : null}

      {viewerPose && props.viewMode === 'free-view' ? (
        <DatFolder title="Camera" defaultHidden>
          <PoseValue label="Origin" value={originValue} />
          <PoseValue label="Angles" value={anglesValue} />
          <PoseValue label="Target" value={targetValue} />
          <DatButton onClick={() => void writeClipboardText(originValue)}>Copy origin</DatButton>
          <DatButton onClick={() => void writeClipboardText(createPoseQuery(viewerPose, props.viewMode))}>
            Copy position query
          </DatButton>
        </DatFolder>
      ) : null}

      {hasModel && modelController && modelData ? (
        <ControllerContainer
          modelData={modelData}
          modelController={modelController}
          onModelStateEdit={handleModelStateEdit}
        >
          {(
            { isPaused, activeAnimationIndex: activeSequenceIndex, visibleSubModelIndices, frame, playbackRate },
            { togglePause, setAnimation, showSubModel, setPlaybackRate, setFrame, setTempPaused }
          ) => {
            const activeSequence = modelData.sequences[activeSequenceIndex]
            const duration = Number((activeSequence.numFrames / activeSequence.fps).toFixed(3))
            const polygonCount = countVisiblePolygons(modelData, visibleSubModelIndices)

            return (
              <React.Fragment>
                <DatFolder title="Playback">
                  <DatSelect
                    label="Sequence"
                    activeItemIndex={activeSequenceIndex}
                    items={modelData.sequences.map(item => item.label)}
                    onChange={seqIndex => setAnimation(seqIndex)}
                  />
                  <DatButton onClick={() => togglePause()}>{isPaused ? 'Resume' : 'Pause'}</DatButton>
                  <DatRange
                    label="Frame"
                    value={frame}
                    min={0}
                    max={activeSequence.numFrames - 1}
                    onChange={value => setFrame(value)}
                    onChangeStart={() => setTempPaused(true)}
                    onChangeComplete={() => setTempPaused(false)}
                  />
                  <DatRange
                    label="Rate"
                    value={Number(playbackRate.toFixed(2))}
                    min={0}
                    max={2}
                    step={0.1}
                    onChange={value => setPlaybackRate(value)}
                  />
                  <SequenceStats>
                    <SequenceStat>
                      Frames
                      <SequenceStatValue>{activeSequence.numFrames}</SequenceStatValue>
                    </SequenceStat>
                    <SequenceStat>
                      FPS
                      <SequenceStatValue>{activeSequence.fps}</SequenceStatValue>
                    </SequenceStat>
                    <SequenceStat>
                      Time
                      <SequenceStatValue>{duration}s</SequenceStatValue>
                    </SequenceStat>
                    <SequenceStat>
                      Polys
                      <SequenceStatValue>{formatCount(polygonCount)}</SequenceStatValue>
                    </SequenceStat>
                  </SequenceStats>
                </DatFolder>

                <DatFolder title="Body parts" defaultHidden>
                  {modelData.bodyParts.map((bodyPart, bodyPartIndex) => (
                    <DatSelect
                      key={bodyPartIndex}
                      label={bodyPart.name}
                      activeItemIndex={visibleSubModelIndices[bodyPartIndex]}
                      items={modelData.subModels[bodyPartIndex].map(subModel => subModel.name)}
                      onChange={subModelIndex => showSubModel(bodyPartIndex, subModelIndex)}
                    />
                  ))}
                </DatFolder>
              </React.Fragment>
            )
          }}
        </ControllerContainer>
      ) : null}
    </StyledDatGui>
  )
}
